import { yupResolver } from '@hookform/resolvers/yup';
import { Col, Form, Modal, Row, message, Alert } from 'antd';
import {
  ProductOutput,
  useGetDonationLazyQuery,
  useCreateProductsMutation,
  CreateProductInput,
  DonationOutput,
  ReasonDiscardProduct,
  ProcessingProductType,
} from 'gql/types';
import { ControlledCheckbox, FormItem, Spin } from 'modules/common/components';
import { BaseModalProps } from 'modules/common/types';
import CategorySelect from 'modules/products/components/CategorySelect';
import ProductExpirationDates from 'modules/products/components/ProductExpirationDates';
import ProductTypeSelect from 'modules/products/components/ProductTypeSelect';
import StorageRecommendations from 'modules/products/components/StorageRecommendations';
import { CATEGORY_ID } from 'modules/products/constants/categoryId';
import { CreateProductModalExtraProps } from 'modules/products/types';
import { isRespinable } from 'modules/products/utils/isRespinable';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DiscardedSection from './components/DiscardedSection';
import DonationDataSection from './components/DonationDataSection';
import { createProductSchema, CreateProductSchema } from './schema';
import styles from './styles.module.scss';

type PropTypes = BaseModalProps & CreateProductModalExtraProps;

const DEFAULT_FORM_VALUES: Partial<CreateProductSchema> = {
  values: [],
  discarded: false,
  quantity: undefined,
  woocommerceCategoryId: undefined,
  woocommerceProductId: undefined,
  respin: undefined,
  isMantelAssigned: undefined,
};

const CreateProductModal: FC<PropTypes> = ({
  hide,
  onCancel,
  onOk,
  onProductsCreation,
  donationId = '',
  ...restModalProps
}) => {
  const { t } = useTranslation('products.CreateProductModal');

  const user = useCurrentUser();
  const productRegion = user.data?.productRegionPreference;

  const [getDonation, getDonationQuery] = useGetDonationLazyQuery({
    onError: (error) => {
      message.error(error.message);
    },
  });

  const donation = getDonationQuery.data?.getDonation;
  const donationDate = donation?.donationDate
    ? new Date(donation?.donationDate)
    : undefined;

  const createProductForm = useForm<CreateProductSchema>({
    resolver: yupResolver(createProductSchema),
    mode: 'onChange',
    defaultValues: DEFAULT_FORM_VALUES as any,
  });

  const {
    control,
    handleSubmit,
    watch,
    resetField,
    getValues,
    reset,
    formState: { isDirty, isValid, touchedFields },
    setValue,
  } = createProductForm;

  const [createProducts, createProductsMutation] = useCreateProductsMutation({
    onCompleted: async (data) => {
      const createdProducts = (data.createProducts as ProductOutput[]) || [];
      message.success(t('productHasBeenCreated'));

      if (donationId) {
        await getDonation({
          fetchPolicy: 'network-only',
          variables: {
            input: {
              id: donationId,
            },
          },
        });
      }

      reset(DEFAULT_FORM_VALUES as any);
      hide();
      onProductsCreation && onProductsCreation(createdProducts);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const isLoading = getDonationQuery.loading || createProductsMutation.loading;

  const quantity = watch('quantity');
  const woocommerceCategoryId = watch('woocommerceCategoryId');
  const discarded = watch('discarded');

  const showRespinSection = isRespinable(woocommerceCategoryId);
  const showMantelAssigned =
    woocommerceCategoryId === CATEGORY_ID.FRESH_FROZEN_PLASMA;

  useEffect(() => {
    if (discarded) {
      setValue('quantity', 1, { shouldDirty: false });
      setValue('values', [0], { shouldDirty: false });
    } else {
      resetField('quantity');
      setValue('values', [], { shouldDirty: false });
    }
  }, [discarded, resetField, setValue]);

  const closeImmediately = (e?: any) => {
    onCancel?.(e);
    reset(DEFAULT_FORM_VALUES as any);
    hide();
  };

  const hasUserTouchedAnything =
    touchedFields && Object.keys(touchedFields).length > 0;

  const onCancelHandler = (e: any) => {
    if (isLoading) {
      return;
    }

    if (!isDirty || !hasUserTouchedAnything) {
      closeImmediately(e);
      return;
    }

    Modal.confirm({
      title: t('unsavedChangesTitle'),
      content: t('unsavedChangesMessage'),
      okText: t('discardChanges'),
      cancelText: t('stayOnPage'),
      onOk: () => closeImmediately(e),
    });
  };

  const createProductHandler = handleSubmit(
    async ({
      values,
      quantity: _,
      discarded,
      discardedReason,
      discardReasonDetails,
      processingType,
      processorId,
      ...restFormData
    }) => {
      const products: CreateProductInput[] = values.map((value) => {
        return {
          value,
          donationId,
          regionPreferenceId: productRegion?.id,
          discardedData: discarded
            ? {
                notes: discardReasonDetails,
                processorId: processorId || '',
                processingType: processingType as ProcessingProductType,
                reason: discardedReason as ReasonDiscardProduct,
              }
            : undefined,
          ...restFormData,
        };
      });
      createProducts({
        variables: {
          input: {
            products,
          },
        },
      });
    }
  );

  const onOkHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    createProductHandler(e);
    onOk?.(e);
  };

  const onDefaultProductTypeChangedHandler = ({
    value,
  }: {
    label: string;
    value: number;
  }) => {
    setValue('woocommerceProductId', value, {
      shouldDirty: false,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    resetField('woocommerceProductId');
    if (woocommerceCategoryId && isRespinable(woocommerceCategoryId)) {
      setValue('respin', false);
    } else {
      resetField('respin');
    }
    if (woocommerceCategoryId === CATEGORY_ID.FRESH_FROZEN_PLASMA) {
      setValue('isMantelAssigned', false);
    } else {
      resetField('isMantelAssigned');
    }
  }, [woocommerceCategoryId, resetField, setValue]);

  useEffect(() => {
    const productValues = getValues('values');
    if (productValues && quantity) {
      resetField('values', { defaultValue: productValues.slice(0, quantity) });
    }
  }, [quantity, getValues, resetField]);

  useEffect(() => {
    if (donationId) {
      getDonation({
        variables: {
          input: {
            id: donationId,
          },
        },
      });
    }
  }, [donationId]);

  return (
    <Modal
      {...restModalProps}
      className={styles.modal}
      onCancel={onCancelHandler}
      onOk={onOkHandler}
      width="100%"
      okText={t('createProduct')}
      okButtonProps={{ disabled: !isValid || !donationId }}
      cancelButtonProps={{ disabled: isLoading }}
      closable={!isLoading}
      keyboard={!isLoading}
      maskClosable={false}
    >
      <Spin spinning={isLoading}>
        <Form>
          <Row gutter={[0, 16]}>
            {!donationId && (
              <Col span={24}>
                <Alert
                  className={styles.errorAlert}
                  message={t('donationDataIsWrong')}
                  type="error"
                />
              </Col>
            )}
            <Col span={24}>
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={8}>
                  <FormItem className={styles.categories}>
                    <CategorySelect
                      controlled={{ control, name: 'woocommerceCategoryId' }}
                    />
                  </FormItem>
                </Col>
                <Col xs={24} sm={8}>
                  <ProductTypeSelect
                    donorBloodType={donation?.donor.bloodType || undefined}
                    productCategoryId={woocommerceCategoryId}
                    controlled={{ control, name: 'woocommerceProductId' }}
                    onChangeDefaultValue={onDefaultProductTypeChangedHandler}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  {donationDate && (
                    <ProductExpirationDates
                      productCategoryId={woocommerceCategoryId}
                      donationDate={donationDate}
                    />
                  )}
                </Col>
              </Row>
            </Col>
            <FormProvider {...createProductForm}>
              <Col span={24}>
                <Row gutter={[16, 16]}>
                  <Col md={12} span={24}>
                    <Row gutter={[0, 16]}>
                      <Col span={24}>
                        <FormItem className={styles.formItem} noStyle>
                          <div
                            className={
                              styles.storageRecommendationsTitleWrapper
                            }
                          >
                            <span className={styles.subtitle}>
                              {t('storageRecommendations')} :
                            </span>
                          </div>
                          <StorageRecommendations
                            controlled={{
                              control,
                              name: 'storageRequirements',
                            }}
                            categoryId={woocommerceCategoryId}
                          />
                        </FormItem>
                      </Col>

                      {donation && (
                        <DonationDataSection
                          donation={donation as DonationOutput}
                        />
                      )}
                    </Row>
                  </Col>
                  <Col md={12} span={24}>
                    <Row gutter={[0, 16]}>
                      <DiscardedSection
                        productRegionPreferenceId={productRegion?.id}
                      />
                      {showRespinSection && (
                        <Col span={24}>
                          <ControlledCheckbox
                            controlled={{ control, name: 'respin' }}
                          >
                            {t('respin')}
                          </ControlledCheckbox>
                        </Col>
                      )}
                      {showMantelAssigned && (
                        <Col span={24}>
                          <ControlledCheckbox
                            controlled={{ control, name: 'isMantelAssigned' }}
                          >
                            {t('isMantelAssigned')}
                          </ControlledCheckbox>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
            </FormProvider>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateProductModal;
